import React from 'react';

/**
 * StatusBadge Component
 * Displays status pills with icons + text (no emojis)
 * Per PocketFiler UI Style Guide Section 7
 */

// Inline SVG icons for status badges (to ensure proper color inheritance)
const CheckIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 6v6l4 2" strokeLinecap="round" />
  </svg>
);

const WarningIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
  </svg>
);

const MailIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" strokeLinecap="round" />
  </svg>
);

const EyeIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" />
  </svg>
);

export function StatusBadge({ status, className = '', showIcon = true }) {
  const statusConfig = {
    // Client invite statuses
    sent: {
      label: 'Sent',
      className: 'bg-muted text-muted-foreground',
      icon: MailIcon
    },
    opened: {
      label: 'Opened',
      className: 'bg-accent/10 text-accent',
      icon: EyeIcon
    },
    in_progress: {
      label: 'In Progress',
      className: 'bg-primary/10 text-primary',
      icon: ClockIcon
    },
    complete: {
      label: 'Complete',
      className: 'bg-success/10 text-success',
      icon: CheckIcon
    },

    // Deal statuses
    draft: {
      label: 'Draft',
      className: 'bg-muted text-muted-foreground',
      icon: DocumentIcon
    },
    active: {
      label: 'Active',
      className: 'bg-success/10 text-success',
      icon: CheckIcon
    },
    pending_documents: {
      label: 'Pending Documents',
      className: 'bg-warning/10 text-warning',
      icon: ClockIcon
    },
    pending_signature: {
      label: 'Pending Signature',
      className: 'bg-warning/10 text-warning',
      icon: ClockIcon
    },
    pending_payment: {
      label: 'Pending Payment',
      className: 'bg-success/10 text-success',
      icon: ClockIcon
    },
    closed: {
      label: 'Closed',
      className: 'bg-muted text-muted-foreground',
      icon: CheckIcon
    },
    cancelled: {
      label: 'Cancelled',
      className: 'bg-destructive/10 text-destructive',
      icon: ErrorIcon
    },

    // Document statuses
    uploaded: {
      label: 'Uploaded',
      className: 'bg-accent/10 text-accent',
      icon: DocumentIcon
    },
    processed: {
      label: 'Verified',
      className: 'bg-success/10 text-success',
      icon: CheckIcon
    },
    requires_review: {
      label: 'Needs Review',
      className: 'bg-warning/10 text-warning',
      icon: WarningIcon
    },
    rejected: {
      label: 'Rejected',
      className: 'bg-destructive/10 text-destructive',
      icon: ErrorIcon
    },

    // Generic statuses
    verified: {
      label: 'Verified',
      className: 'bg-success/10 text-success',
      icon: CheckIcon
    },
    warning: {
      label: 'Attention',
      className: 'bg-warning/10 text-warning',
      icon: WarningIcon
    },
    error: {
      label: 'Error',
      className: 'bg-destructive/10 text-destructive',
      icon: ErrorIcon
    },
    pending: {
      label: 'Pending',
      className: 'bg-muted text-muted-foreground',
      icon: ClockIcon
    },
    // Addition for accepted invite status
    accepted: {
      label: 'Accepted',
      className: 'bg-success/10 text-success',
      icon: CheckIcon
    }
  };

  // Capitalize fallback label function
  const formatFallbackLabel = (str) => {
    if (!str) return 'Unknown';
    return String(str)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const normalizedStatus = status ? String(status).toLowerCase() : '';

  const config = statusConfig[normalizedStatus] || {
    label: formatFallbackLabel(status),
    className: 'bg-muted text-muted-foreground',
    icon: DocumentIcon
  };

  const IconComponent = config.icon;

  return (
    <span
      data-testid="status-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className} ${className}`}
    >
      {showIcon && IconComponent && <IconComponent />}
      {config.label}
    </span>
  );
}

// Export a simpler version without icon for compact spaces
export function StatusDot({ status, className = '' }) {
  const statusColors = {
    sent: 'bg-muted-foreground',
    opened: 'bg-accent',
    in_progress: 'bg-primary',
    complete: 'bg-success',
    draft: 'bg-muted-foreground',
    active: 'bg-success',
    pending_documents: 'bg-warning',
    pending_signature: 'bg-warning',
    pending_payment: 'bg-success',
    closed: 'bg-muted-foreground',
    cancelled: 'bg-destructive',
    uploaded: 'bg-accent',
    processed: 'bg-success',
    requires_review: 'bg-warning',
    rejected: 'bg-destructive',
    accepted: 'bg-success',
  };

  const normalizedStatus = status ? String(status).toLowerCase() : '';
  const color = statusColors[normalizedStatus] || 'bg-muted-foreground';

  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${color} ${className}`}
      data-testid="status-dot"
    />
  );
}
